// Display formatting shared across every G1 Health section. Pure and
// venue-agnostic — no thresholds, no tone decisions, nothing that knows what a
// healthy number looks like. Those live with the section that judges them.

/** Splits an account key ("org:<uuid>" | "user:<uuid>") into its scope and a
 *  short id, eliding the middle of anything longer than 10 chars so a UUID fits
 *  a table cell without a tooltip. */
export function shortKey(key: string): { scope: string; id: string } {
  const [scope, id = ''] = key.split(':');
  return { scope, id: id.length > 10 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id };
}

/** The org id inside an account key, or null for a personal ("user:") account.
 *  Returning null rather than throwing is what lets the org drill-down filter
 *  personal accounts out by the same predicate that selects org ones. */
export function orgIdOf(key: string): string | null {
  return key.startsWith('org:') ? key.slice(4) : null;
}

/** The org drill-down predicate: does this org id fall inside the current
 *  selection? `'all'` admits everything including personal (`user:`) accounts,
 *  which have no org id at all; selecting a specific org therefore excludes them,
 *  and that is deliberate rather than an oversight about nulls.
 *
 *  Shared because the page and the reject panel each filter their own list by it,
 *  and a drill-down that meant two different things in two places would be a
 *  quiet way to show one org's numbers under another org's name. */
export const orgMatcher = (selectedOrg: string) => (orgId: string | null) =>
  selectedOrg === 'all' || orgId === selectedOrg;

/** Compact age: seconds under a minute, then `m` + zero-padded seconds. */
export function fmtAge(ms: number): string {
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m${String(s % 60).padStart(2, '0')}`;
}

/**
 * How old a relay-stamped moment was when the relay answered, or why that cannot
 * be said. `missing` is the relay reporting nothing yet (null) — a real state, not
 * a failure. `invalid` and `future` are evidence the page cannot use; neither may be
 * rendered as an age, and in particular a future stamp must never become "0s ago".
 */
export type RelayAge =
  | { kind: 'age'; ms: number }
  | { kind: 'missing' }
  | { kind: 'invalid' }
  | { kind: 'future' };

// Exactly what `Date.prototype.toISOString()` emits, which is the only way the
// relay writes these fields (settlementSweeper.ts / positionStates.ts, ISO since
// the day both getters were added). Anything else — a bare epoch number included —
// is not this contract and reads as invalid rather than being guessed at.
const RELAY_ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

/** Epoch ms for a relay ISO timestamp, or null. The round-trip is the calendar
 *  check: `Date.parse` can quietly roll `02-30` into March and `24:00` into the next
 *  day (measured in V8), so a parse that succeeds is not proof the date exists. */
export function parseRelayIso(value: unknown): number | null {
  if (typeof value !== 'string' || !RELAY_ISO.test(value)) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) && new Date(ms).toISOString() === value ? ms : null;
}

/**
 * Age of a relay ISO timestamp measured against the SAME relay's `serverTs` — both
 * clocks are that one process's wall clock, so browser skew never enters the
 * number. Nothing here consults `Date.now()`.
 */
export function relayAge(serverTs: unknown, stamp: unknown): RelayAge {
  if (stamp === null || stamp === undefined) return { kind: 'missing' };
  return ageAt(serverTs, parseRelayIso(stamp));
}

/** The same contract for a relay field that is an epoch-ms NUMBER by design — the
 *  fills poller's `lastPollAt` and the write-429 tape's `ts`, both `Date.now()` in the
 *  same process. 0 is how the poller would say "never", so it reads as missing
 *  rather than as 1970. */
export function relayEpochAge(serverTs: unknown, epochMs: unknown): RelayAge {
  if (epochMs === null || epochMs === undefined || epochMs === 0) return { kind: 'missing' };
  return ageAt(serverTs, typeof epochMs === 'number' && Number.isFinite(epochMs) && epochMs > 0 ? epochMs : null);
}

function ageAt(serverTs: unknown, at: number | null): RelayAge {
  if (at === null || typeof serverTs !== 'number' || !Number.isFinite(serverTs) || serverTs <= 0) {
    return { kind: 'invalid' };
  }
  const ms = serverTs - at;
  return ms < 0 ? { kind: 'future' } : { kind: 'age', ms };
}

/** "2m08 ago", or a plain phrase saying the `event`'s time could not be used
 *  ("last run time unreadable"). `missing` has no universal wording ("no run yet"
 *  vs "none seen"), so callers handle it before asking for text. */
export function relayAgeText(age: Exclude<RelayAge, { kind: 'missing' }>, event: string): string {
  if (age.kind === 'age') return `${fmtAge(age.ms)} ago`;
  return age.kind === 'future' ? `${event} time is ahead of the relay clock` : `${event} time unreadable`;
}
